import clsx from "clsx";

import type { DeployedUnit, Phase } from "../api/types";
import { Badge } from "../components/ui";
import { unitDesignation } from "../i18n/designation";
import { t } from "../i18n/i18n";
import { unitMissionName } from "../i18n/vocabulary";
import { symbolSvg } from "../map/units/symbols";
import { useDeployment } from "../map/units/useDeployment";
import { MissionChevron, MissionThumb, missionImageOf } from "../panels/MissionMenu";
import { standingTaskLabel } from "../panels/missionMenuModel";
import { useUi } from "../state/ui";

/** Mission d'une unité par temps (panneau Manœuvre) et localisation d'une unité au temps choisi. Les listes
 *  « Missions », « Actions ennemies » et « Incidents » des Objets de la mission ont été retirées le 28/10/2026 avec
 *  leur entrée de la barre latérale (demande d'Alexis). */

const PHASES: Phase[] = ["T1", "T2", "T3"];

/** Localise une unité au temps choisi : ce temps s'affiche sur la carte, l'unité est sélectionnée et centrée. */
export function useLocate() {
  const select = useUi((s) => s.select);
  const flyTo = useUi((s) => s.requestFlyTo);
  const setPhase = useUi((s) => s.setPhase);
  return (unit: DeployedUnit, phase: Phase) => {
    const position = unit.positions[phase];
    if (!position) return;
    setPhase(phase);
    select({ layer: "unit", code: unit.code, lngLat: [position.lon, position.lat] });
    flyTo([position.lon, position.lat], 13);
  };
}

const title = (unit: DeployedUnit) => unitDesignation(unit.display_name || unit.designation);

/** Bulle et nom accessible du point « modifiée » : la mission doctrinale que le choix remplace. */
function overriddenTitle(doctrinal: string | null | undefined): string {
  const base = t("Mission choisie à la main");
  if (doctrinal === null || doctrinal === undefined) return base;
  return `${base} · ${t("doctrinale : {mission}", { mission: doctrinal ? unitMissionName(doctrinal) : t("Sans mission") })}`;
}

/** Une unité et sa mission à chaque temps ; le temps affiché sur la carte est surligné. Chaque ligne a deux
 *  boutons frères (docs/14 §2.1) : « aller au temps » (affiche ce temps et localise l'unité) et le chevron du menu
 *  de mission. `thumbnails` : miniature du symbole de mission devant le libellé (panneau Manœuvre). */
export function UnitTimeline({
  unit,
  phase,
  onLocate,
  thumbnails = false,
}: {
  unit: DeployedUnit;
  phase: Phase;
  onLocate: (p: Phase) => void;
  thumbnails?: boolean;
}) {
  const selection = useUi((s) => s.selection);
  const { data: deployment } = useDeployment();
  const selected = selection?.layer === "unit" && selection.code === unit.code;
  // PC, trains de combat (A95) : même tâche à chaque temps, dite une fois sous le nom ; les lignes ne portent que
  // la localité.
  const standing = standingTaskLabel(unit);
  return (
    <li className={clsx("rounded-lg px-2 py-1.5 transition-colors", selected && "bg-selected")}>
      <div className="flex min-h-6 items-center gap-2">
        <span
          className="flex h-4 w-6 shrink-0 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
          dangerouslySetInnerHTML={{ __html: symbolSvg(unit.sidc, 13) }}
        />
        <span className="min-w-0 flex-1 break-words text-ui leading-snug font-semibold text-ink">
          {title(unit)}
        </span>
      </div>
      {standing && <p className="pl-8 text-meta leading-snug text-ink-muted">{standing}</p>}
      <ul className="mt-0.5 space-y-px pl-7">
        {PHASES.map((p) => {
          const position = unit.positions[p];
          const mission = standing
            ? null
            : position
              ? unitMissionName(position.mission_label || position.mission) || t("Sans mission")
              : null;
          const current = p === phase;
          const effort = unit.effort.includes(p);
          return (
            <li
              key={p}
              className={clsx(
                "flex min-h-8 items-center gap-1 rounded-md py-1.5 pr-0.5 pl-1.5 transition-colors",
                current ? "bg-selected" : position && "hover:bg-hover",
              )}
            >
              <button
                type="button"
                disabled={!position}
                onClick={() => onLocate(p)}
                title={
                  position
                    ? `${standing ? t("{phase} : {mission}", { phase: p, mission: position.locality || t("Lieu-dit sans nom") }) : `${t("{phase} : {mission}", { phase: p, mission })}${position.locality ? ` — ${position.locality}` : ""}`}\n${t("Cliquer pour afficher {phase} et localiser l'unité", { phase: p })}`
                    : t("Absente de la carte en {phase}", { phase: p })
                }
                className={clsx(
                  "focus-inset flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-left text-meta",
                  !position && "cursor-default opacity-40",
                )}
              >
                <span className={clsx("w-5 shrink-0 font-semibold tabular-nums", current ? "text-ink" : "text-ink-muted")}>{p}</span>
                {thumbnails && position && !standing && (
                  <MissionThumb image={missionImageOf(deployment, unit, position.mission)} label={mission ?? ""} />
                )}
                {standing ? (
                  <span className="min-w-0 break-words font-medium text-ink">
                    {position ? position.locality || t("Lieu-dit sans nom") : "—"}
                  </span>
                ) : (
                  <span className="shrink-0 font-medium text-ink">{mission ?? "—"}</span>
                )}
                {position?.overridden && (
                  <span
                    role="img"
                    className="size-1.5 shrink-0 rounded-full bg-accent"
                    title={overriddenTitle(position.doctrinal_mission)}
                    aria-label={overriddenTitle(position.doctrinal_mission)}
                  />
                )}
                {!standing && position?.locality && (
                  <span className="min-w-0 break-words text-ink-muted">{position.locality}</span>
                )}
                {effort && (
                  <Badge dot size="sm" tone="accent" className="ml-auto">
                    {t("Effort")}
                  </Badge>
                )}
              </button>
              <MissionChevron unit={unit} phase={p} />
            </li>
          );
        })}
      </ul>
    </li>
  );
}
